import { useLayoutEffect, useRef, useState } from 'react';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useTheme } from '@styledComponents';
import { useIsMounted } from '@hooks/useIsMounted';
import ScrollMagic from 'scrollmagic';
import { gsap } from 'gsap';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { useLottieAnimation } from '@hooks/useLottieAnimation';
import { AnimationItem } from 'lottie-web';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';

interface InitTimelineRefs {
  universalRegistryTextPairRef: React.RefObject<HTMLDivElement>;
  iconsContainerRef: React.RefObject<HTMLDivElement>;
}

interface DelayedAnimationsRefs {
  giftsAnimationCall: ReturnType<typeof gsap.delayedCall> | null;
  cashAnimationCall: ReturnType<typeof gsap.delayedCall> | null;
  experiencesAnimationCall: ReturnType<typeof gsap.delayedCall> | null;
  charityAnimationCall: ReturnType<typeof gsap.delayedCall> | null;
}

const initTimeline = (
  scrollController: ScrollMagic.Controller | undefined,
  isMounted: boolean,
  { universalRegistryTextPairRef, iconsContainerRef }: InitTimelineRefs,
  animationRefs: {
    giftsAnimationRef: React.RefObject<AnimationItem | null>;
    cashAnimationRef: React.RefObject<AnimationItem | null>;
    experiencesAnimationRef: React.RefObject<AnimationItem | null>;
    charityAnimationRef: React.RefObject<AnimationItem | null>;
  }
) => {
  let giftsAnimationCall: ReturnType<typeof gsap.delayedCall> | null = null;
  let cashAnimationCall: ReturnType<typeof gsap.delayedCall> | null = null;
  let experiencesAnimationCall: ReturnType<typeof gsap.delayedCall> | null = null;
  let charityAnimationCall: ReturnType<typeof gsap.delayedCall> | null = null;

  const universalRegistryTextPairRefEl = universalRegistryTextPairRef.current;
  const iconsContainerEl = iconsContainerRef.current;
  const { giftsAnimationRef, cashAnimationRef, experiencesAnimationRef, charityAnimationRef } = animationRefs;

  if (scrollController && isMounted && universalRegistryTextPairRefEl && iconsContainerEl) {
    giftsAnimationCall = gsap.delayedCall(0.5, () => giftsAnimationRef.current?.goToAndPlay(0), []);
    cashAnimationCall = gsap.delayedCall(1, () => cashAnimationRef.current?.goToAndPlay(0), []);
    experiencesAnimationCall = gsap.delayedCall(1.5, () => experiencesAnimationRef.current?.goToAndPlay(0, true), []);
    charityAnimationCall = gsap.delayedCall(2, () => charityAnimationRef.current?.goToAndPlay(0, true), []);
  }
  return { giftsAnimationCall, cashAnimationCall, experiencesAnimationCall, charityAnimationCall };
};

export const useRegistryController = () => {
  const [isScrolledIntoView, setScrolledIntoView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const universalRegistryTextPairRef = useRef<HTMLDivElement>(null);
  const iconsContainerRef = useRef<HTMLDivElement>(null);
  //
  const giftsLottieRef = useRef<HTMLDivElement>(null);
  const cashLottieRef = useRef<HTMLDivElement>(null);
  const experiencesLottieRef = useRef<HTMLDivElement>(null);
  const charityLottieRef = useRef<HTMLDivElement>(null);
  //
  const scrollController = useScrollController();
  const theme = useTheme();
  const isMounted = useIsMounted();
  const isMobile = useThemeMediaQuery('below_sm');
  //
  const { animationRef: giftsAnimationRef } = useLottieAnimation(
    {
      path: cloudAssetUrl('registry-v2/universal-registry/bodymovin/gifts/data.json'),
      renderer: 'svg',
      loop: false,
      autoplay: false,
      name: 'registry hero gifts',
      rendererSettings: {
        progressiveLoad: false,
        preserveAspectRatio: 'xMidYMid slice',
      },
    },
    { containerRef: giftsLottieRef, isLoadInitially: true }
  );
  //
  const { animationRef: cashAnimationRef } = useLottieAnimation(
    {
      path: cloudAssetUrl('registry-v2/universal-registry/bodymovin/cash/data.json'),
      renderer: 'svg',
      loop: false,
      autoplay: false,
      name: 'registry hero cash',
      rendererSettings: {
        progressiveLoad: false,
        preserveAspectRatio: 'xMidYMid slice',
      },
    },
    { containerRef: cashLottieRef, isLoadInitially: true }
  );
  //
  const { animationRef: experiencesAnimationRef } = useLottieAnimation(
    {
      path: cloudAssetUrl('registry-v2/universal-registry/bodymovin/experiences/data.json'),
      renderer: 'svg',
      loop: false,
      autoplay: false,
      name: 'registry hero experiences',
      rendererSettings: {
        progressiveLoad: false,
        preserveAspectRatio: 'xMidYMid slice',
      },
    },
    { containerRef: experiencesLottieRef, isLoadInitially: true }
  );
  //
  const { animationRef: charityAnimationRef } = useLottieAnimation(
    {
      path: cloudAssetUrl('registry-v2/universal-registry/bodymovin/charity/data.json'),
      renderer: 'svg',
      loop: false,
      autoplay: false,
      name: 'registry hero charity',
      rendererSettings: {
        progressiveLoad: false,
        preserveAspectRatio: 'xMidYMid slice',
      },
    },
    { containerRef: charityLottieRef, isLoadInitially: true }
  );

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    const heroTextContainerEle = universalRegistryTextPairRef.current;
    const elRefs: InitTimelineRefs = {
      universalRegistryTextPairRef,
      iconsContainerRef,
    };
    const animationRefs = {
      giftsAnimationRef,
      cashAnimationRef,
      experiencesAnimationRef,
      charityAnimationRef,
    };
    let timeline: DelayedAnimationsRefs = {
      giftsAnimationCall: null,
      cashAnimationCall: null,
      experiencesAnimationCall: null,
      charityAnimationCall: null,
    };
    let timeout: ReturnType<typeof setTimeout>;

    // kill the active animation when component unmounts
    const seenKiller = () => {
      if (timeline) {
        timeline?.giftsAnimationCall?.progress(0);
        timeline?.cashAnimationCall?.progress(0);
        timeline?.experiencesAnimationCall?.progress(0);
        timeline?.charityAnimationCall?.progress(0);

        timeline?.giftsAnimationCall?.kill();
        timeline?.cashAnimationCall?.kill();
        timeline?.experiencesAnimationCall?.kill();
        timeline?.charityAnimationCall?.kill();
      }
    };

    // kill and restart animation when window resige even called
    const refreshTimeline = () => {
      if (scrollController) {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          const scrollPos = scrollController?.scrollPos() as number;
          seenKiller();
          initTimeline(scrollController, isMounted, elRefs, animationRefs);
          if (scene && heroTextContainerEle) {
            scene.update();
          }
          scrollController.scrollTo(scrollPos);
        }, 750);
      }
    };

    // initiate the animation when scoll reached
    const initScene = () => {
      if (heroTextContainerEle && scrollController) {
        scene = new ScrollMagic.Scene({
          triggerElement: heroTextContainerEle,
          triggerHook: 'onEnter',
          duration: '60%',
        })
          .addTo(scrollController)
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            if (event.progress === 1) {
              timeline = initTimeline(scrollController, isMounted, elRefs, animationRefs);
            }
          });
      }
    };

    initScene();
    const clearScene = () => {
      seenKiller();
      if (scene) {
        scene.destroy(true);
      }
    };
    return () => {
      clearScene();
      window.removeEventListener('resize', refreshTimeline);
    };
  }, [scrollController, isMounted, isMobile, theme, giftsAnimationRef, cashAnimationRef, experiencesAnimationRef, charityAnimationRef]);
  return {
    sectionRef,
    universalRegistryTextPairRef,
    iconsContainerRef,
    giftsLottieRef,
    cashLottieRef,
    experiencesLottieRef,
    charityLottieRef,
    isScrolledIntoView,
    setScrolledIntoView,
  };
};
